import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Checkbox,
  Label,
  Icon,
  enums as e,
  Popover,
  usePopover,
  ClickAwayListener,
} from 'edrolo-ui';
import s from './styles.scss';

interface Option {
  value: string;
  label: string;
}

export interface Props {
  label: React.ReactNode;
  options: Option[];
  value?: string[]; // controlled
  defaultValue?: string[]; // uncontrolled initial value
  onChange: (values: string[]) => void;
}

export const MultiSelect = ({
  label,
  options,
  value,
  defaultValue,
  onChange,
}: Props) => {
  const { isOpen, anchor, togglePopover, closePopover, openPopover } =
    usePopover();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLUListElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(
    undefined
  );
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      setPopoverWidth(buttonRef.current.offsetWidth);
    }
  }, [isOpen]);

  // Reset focused index when popover closes
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  const isControlled = value !== undefined;
  const [internalSelected, setInternalSelected] = useState<string[]>(
    defaultValue ?? []
  );
  const wasControlled = useRef(isControlled);
  if (wasControlled.current !== isControlled) {
    console.warn(
      'MultiSelect should not switch between controlled and uncontrolled modes.'
    );
    // Update ref so you don’t spam in future renders:
    wasControlled.current = isControlled;
  }
  const selectedValues = isControlled ? value! : internalSelected;
  const updateSelectedValues = useCallback(
    (newValues: string[]) => {
      if (!isControlled) {
        setInternalSelected(newValues);
      }
      onChange(newValues);
    },
    [onChange, isControlled]
  );

  const toggleOption = useCallback(
    (optionIndex: number) => {
      if (optionIndex === 0) {
        // "All" option
        if (selectedValues.length === options.length) {
          updateSelectedValues([]);
        } else {
          updateSelectedValues(options.map((option) => option.value));
        }
      } else if (optionIndex > 0 && optionIndex <= options.length) {
        // Individual option
        const option = options[optionIndex - 1];
        if (selectedValues.includes(option.value)) {
          updateSelectedValues(
            selectedValues.filter((val) => val !== option.value)
          );
        } else {
          updateSelectedValues([...selectedValues, option.value]);
        }
      }
    },
    [options, selectedValues, updateSelectedValues]
  );

  const focusOption = useCallback((index: number) => {
    if (popoverRef.current) {
      const options = popoverRef.current.querySelectorAll(
        'li[role="option"], li:first-child'
      );
      const targetOption = options[index] as HTMLElement;
      if (targetOption) {
        const checkbox = targetOption.querySelector(
          'input[type="checkbox"]'
        ) as HTMLInputElement;
        if (checkbox) {
          checkbox.focus();
        }
      }
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) {
            const syntheticEvent = {
              currentTarget: event.currentTarget,
            } as unknown as React.MouseEvent<HTMLElement>;
            openPopover(syntheticEvent);
            // Focus on the last item when opening with ArrowUp
            setTimeout(() => {
              const lastIndex = options.length; // +1 for "All" option
              setFocusedIndex(lastIndex);
              focusOption(lastIndex);
            }, 0);
          } else {
            const lastIndex = options.length; // +1 for "All" option
            setFocusedIndex(lastIndex);
            focusOption(lastIndex);
          }
          break;

        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            const syntheticEvent = {
              currentTarget: event.currentTarget,
            } as unknown as React.MouseEvent<HTMLElement>;
            openPopover(syntheticEvent);
            // Focus on the first item ("All") when opening with ArrowDown
            setTimeout(() => {
              setFocusedIndex(0);
              focusOption(0);
            }, 0);
          } else {
            setFocusedIndex(0);
            focusOption(0);
          }
          break;

        case 'Tab':
          if (isOpen) {
            // When popover is open, close it and let natural tab flow continue
            closePopover();
          }
          break;

        case 'Escape':
          if (isOpen) {
            event.preventDefault();
            closePopover();
            buttonRef.current?.focus();
          }
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!isOpen) {
            const syntheticEvent = {
              currentTarget: event.currentTarget,
            } as unknown as React.MouseEvent<HTMLElement>;
            openPopover(syntheticEvent);
          }
          break;
      }
    },
    [isOpen, openPopover, closePopover, options.length, focusOption]
  );

  const handlePopoverKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLUListElement>) => {
      const totalOptions = options.length + 1; // +1 for "All" option

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const newIndex = prev <= 0 ? totalOptions - 1 : prev - 1;
            focusOption(newIndex);
            return newIndex;
          });
          break;

        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => {
            const newIndex = prev >= totalOptions - 1 ? 0 : prev + 1;
            focusOption(newIndex);
            return newIndex;
          });
          break;

        case 'Escape':
          event.preventDefault();
          closePopover();
          buttonRef.current?.focus();
          break;

        case 'Tab': {
          event.preventDefault();
          const totalOptions = options.length + 1; // +1 for "All" option
          const isLastItem = focusedIndex === totalOptions - 1;

          if (event.shiftKey) {
            // Shift+Tab: always close popover and return focus to button
            closePopover();
            setTimeout(() => buttonRef.current?.focus(), 0);
          } else if (isLastItem) {
            // Tab from last item: close popover and let focus move to next element naturally
            closePopover();
          } else {
            // Tab from any other item: close popover and return focus to button
            closePopover();
            setTimeout(() => buttonRef.current?.focus(), 0);
          }
          break;
        }

        case 'Enter':
        case ' ':
          event.preventDefault();
          // Activate the currently focused option and close popover
          toggleOption(focusedIndex);
          break;
      }
    },
    [options.length, focusedIndex, toggleOption, focusOption, closePopover]
  );

  return (
    <ClickAwayListener onClickAway={closePopover}>
      <div className={s.multiSelect}>
        <button
          ref={buttonRef}
          onClick={togglePopover}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={`${label}${
            selectedValues.length > 0
              ? `, ${selectedValues.length} selected`
              : ''
          }`}
        >
          <span>
            {label}&nbsp;
            {selectedValues.length > 0 && `(${selectedValues.length})`}
          </span>
          <Icon name={e.Icons.CHEVRON_DOWN} />
        </button>

        <Popover isOpen={isOpen} anchor={anchor} closePopover={closePopover}>
          <ul
            role="listbox"
            className={s.ul}
            style={{ width: popoverWidth }}
            ref={popoverRef}
            onKeyDown={handlePopoverKeyDown}
          >
            <li
              className={focusedIndex === 0 ? s.focused : ''}
              role="option"
              aria-selected={selectedValues.length === options.length}
            >
              <Label>
                <Checkbox
                  checked={selectedValues.length === options.length}
                  onChange={() => toggleOption(0)}
                />
                All
              </Label>
            </li>
            {options.map((option, i) => (
              <li
                key={i}
                role="option"
                className={focusedIndex === i + 1 ? s.focused : ''}
                aria-selected={selectedValues.includes(option.value)}
              >
                <Label>
                  <Checkbox
                    checked={selectedValues.includes(option.value)}
                    onChange={() => toggleOption(i + 1)}
                  />
                  {option.label}
                </Label>
              </li>
            ))}
          </ul>
        </Popover>
      </div>
    </ClickAwayListener>
  );
};
